import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faSearch } from "@fortawesome/pro-solid-svg-icons/faSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AutoComplete from "antd/es/auto-complete";
import Input from "antd/es/input";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { smartPlannerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import SearchResult from "@app/scene/activity/component/ActivityCalendarPage/components/Sidebar/Toolbar/SearchResult";
import {
  areSearchResultsLoading,
  getSearchResults,
} from "@app/scene/activity/component/ActivityCalendarPage/store";
import {
  findActivities,
  goToActivity,
} from "@app/scene/activity/component/ActivityCalendarPage/store/actions";
import styles from "@app/scene/activity/component/ActivityListPage/component/FilterBar/QuickSearch.module.scss";
import { RootState } from "@app/store/rootReducer";

interface Props {
  className?: string;
  findActivities: (payload: { query: string }) => void;
  goToActivity: (payload: { activity: Activity; callback?: () => void }) => void;
  loading?: boolean;
  searchResults: Activity[];
}

const ActivitiesSearch: React.FC<Props> = ({
  className,
  findActivities,
  goToActivity,
  loading,
  searchResults,
}) => {
  const intl = useIntl();

  const [value, setValue] = useState("");

  const doSearch = useDebouncedCallback([
    useCallback(
      (query: string) => {
        if (query.trim().length) {
          findActivities({ query });
        }
      },
      [findActivities]
    ),
    500,
  ]);

  const handleSelect = useCallback(
    (value: string) => {
      const activityId = parseInt(value, 10);
      const activity = searchResults.find(({ id }) => id === activityId);

      if (activity) {
        setValue(activity.name);
        goToActivity({
          activity,
          callback: () => {
            setTimeout(() => {
              const element = document.querySelector(`.act_${activity.id}.fc-event`);
              if (element && element instanceof HTMLElement) {
                element.scrollIntoView({ block: "center" });
                setTimeout(() => element.click(), 0);
              }
            }, 0);
          },
        });
      }
    },
    [goToActivity, searchResults]
  );

  const options = useMemo(
    () =>
      searchResults.map((activity) => ({
        label: <SearchResult activity={activity} />,
        value: String(activity.id),
      })),
    [searchResults]
  );

  const handleClick = useCallback(() => {
    smartPlannerViewAnalytics.clicked(["Search"]);
  }, []);

  return (
    <AutoComplete
      className={cn(styles.container, className)}
      dropdownMatchSelectWidth={false}
      onChange={setValue}
      onClick={handleClick}
      onSearch={doSearch}
      onSelect={handleSelect}
      options={options}
      placeholder={intl.formatMessage({
        id: "activities.calendarView.sidebar.quickSearch.placeholder",
        defaultMessage: "Search",
        description: "Activity calendar view's quick search field placeholder",
      })}
      value={value}
    >
      <Input
        suffix={
          loading ? (
            <LoadingSpinner mini />
          ) : (
            <FontAwesomeIcon className={styles.searchIcon} icon={faSearch} />
          )
        }
      />
    </AutoComplete>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: areSearchResultsLoading(state),
  searchResults: getSearchResults(state),
});

const mapDispatchToProps = {
  findActivities: findActivities.request,
  goToActivity,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivitiesSearch);
